import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  BookOpenIcon,
  ChevronDownIcon,
  IconSizes,
  LogOutIcon,
  SettingsIcon,
  ToolIcon,
} from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@newcore/ui/dropdown-menu';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Text } from '@newcore/ui/typography';
import { ROUTES } from '@/common/auth-routes';
import { buildTenantPath } from '@/common/tenant-utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { getExtensionId } from '@/config/env-config';
import { useTenant } from '@/contexts/use-tenant';
import { useAuth } from '@/hooks/use-auth';
import { useMcpTesterVisible } from '@/hooks/use-mcp-tester-visible';
import type {
  ExtensionLogoutUserRequest,
  ExtensionLogoutUserResponse,
} from '@/types/extension-sync.types';
import { logError } from '@/utils/error-logger';
import { getUserInitials } from '@/utils/user-utils';

const logOutExtension = async () => {
  if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
    const extensionId = getExtensionId();
    const payload: ExtensionLogoutUserRequest = {
      action: 'logoutUser',
    };

    chrome.runtime.sendMessage(
      extensionId,
      payload,
      (response?: ExtensionLogoutUserResponse) => {
        if (chrome.runtime.lastError) {
          return;
        }

        if (response?.status === 'error') {
          logError(response.error, 'extension-sync');
        }
      }
    );
  }
};

export const UserMenu = React.memo(() => {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const showMcpTester = useMcpTesterVisible();
  const [isOpen, setIsOpen] = useState(false);

  const initials = getUserInitials(user);

  const handleLogout = async () => {
    logOutExtension();
    await logout();
  };

  const handleAccountSettings = () => {
    navigate('account-settings');
  };

  const handleDocs = () => {
    navigate(buildTenantPath(tenant, ROUTES.DOCS));
  };

  const handleMcpTester = () => {
    navigate(buildTenantPath(tenant, ROUTES.MCP_TESTER));
  };

  return (
    <DropdownMenu onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className='relative flex shrink-0 rounded-full transition-opacity hover:opacity-90'
          aria-label={`Account — ${user?.name ?? 'User'}`}
          title={user?.name ?? 'User'}
          data-testid='user-menu-trigger'
        >
          <Avatar className='size-9'>
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span
            aria-hidden='true'
            className='absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-tertiary text-secondary ring-[1.5px] ring-white'
          >
            <ChevronDownIcon
              size={IconSizes.XS}
              strokeWidth={2.5}
              className={cn('transition-transform', isOpen && 'rotate-180')}
            />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-60' align='end' forceMount>
        <DropdownMenuLabel className='flex flex-col gap-0.5'>
          <Text weight={FontWeights.SEMIBOLD} className='truncate text-primary'>
            {user?.name ?? 'User'}
          </Text>
          <Text className='truncate text-tertiary'>
            {user?.email ?? 'No primary email'}
          </Text>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleAccountSettings}
          data-testid='account-settings-menu-item'
        >
          <SettingsIcon />
          <span>Account Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDocs} data-testid='docs-menu-item'>
          <BookOpenIcon />
          <span>Documentation</span>
        </DropdownMenuItem>
        {showMcpTester && (
          <DropdownMenuItem
            onClick={handleMcpTester}
            data-testid='mcp-tester-menu-item'
          >
            <ToolIcon />
            <span>MCP Tester</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOutIcon />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

UserMenu.displayName = 'UserMenu';
